import React, { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { UnifiedTransaction } from '../types/payment';
import { SalesAPI, EditHistoryEntry, ApprovalAPI, ProcessingInfo } from '../services/api';
import { AlertService } from '../services/AlertService';
import { ProcessingSection } from './ProcessingSection';
import { TransactionComments } from './TransactionComments';
import { CustomerDocumentsSection } from './CustomerDocumentsSection';

// Slim, processing-only details panel for the processor pages. Shows just the
// company/processor-side fields (via ProcessingSection) plus documents and the
// activity log — none of the universal panel's card/3DS/void-refund sections.
interface Props {
  transaction: UnifiedTransaction | null;
  isOpen: boolean;
  onClose: () => void;
  decryptedData?: any;
  onDecryptData?: (transactionId: string, reason: string) => void;
  onEdited?: () => void;
}

const editSourceLabel = (s?: string | null): string =>
  s === 'processing_section' ? 'Processing' : s === 'sale_edit' ? 'Details edit' : 'Edit';
// Lists (e.g. services) join with commas; objects (e.g. service -> provider) read "Gas: Columbia Gas".
const fmtAuditVal = (v: any): string => {
  if (v === null || v === undefined || v === '') return '—';
  if (Array.isArray(v)) return v.length ? v.map(fmtAuditVal).join(', ') : '—';
  if (typeof v === 'object') {
    const parts = Object.entries(v).map(([k, x]) => `${k}: ${fmtAuditVal(x)}`);
    return parts.length ? parts.join(', ') : '—';
  }
  return String(v);
};

export const ProcessingDetailsPanel: React.FC<Props> = ({
  transaction, isOpen, onClose, decryptedData, onDecryptData, onEdited,
}) => {
  const [showReason, setShowReason] = useState(false);
  const [reason, setReason] = useState('');
  const [editHistory, setEditHistory] = useState<EditHistoryEntry[]>([]);
  // Latest processing info (from ProcessingSection) for the Deposit toggle in the header
  const [pinfo, setPinfo] = useState<ProcessingInfo | null>(null);
  const [depositSaving, setDepositSaving] = useState(false);

  const recordId = transaction
    ? ((transaction as any).pending_sale_id || (transaction as any).transaction_id || transaction.id)
    : '';
  const txIdForDecrypt = transaction ? ((transaction as any).transaction_id || transaction.id) : '';

  const loadEditHistory = useCallback(() => {
    if (!recordId) { setEditHistory([]); return; }
    SalesAPI.getEditHistory(recordId).then(setEditHistory).catch(() => setEditHistory([]));
  }, [recordId]);

  useEffect(() => { setPinfo(null); }, [recordId, isOpen]);

  useEffect(() => {
    if (!isOpen || !transaction) { setEditHistory([]); setShowReason(false); setReason(''); return; }
    loadEditHistory();
  }, [isOpen, transaction, loadEditHistory]);

  const toggleDeposit = async (on: boolean) => {
    setDepositSaving(true);
    try {
      setPinfo(await ApprovalAPI.setDeposit(recordId, on));
      onEdited?.();
      loadEditHistory();
    } catch (e: any) {
      AlertService.error(e?.message || 'Failed to update Deposit');
    } finally {
      setDepositSaving(false);
    }
  };

  if (!isOpen || !transaction) return null;

  const name = `${transaction.first_name || ''} ${transaction.last_name || ''}`.trim() || 'Customer';
  const initials = (name === 'Customer' ? 'C' : name.split(/\s+/).map((p) => p[0]).slice(0, 2).join('')).toUpperCase();
  const status = (transaction as any).processing_status as string | undefined;
  const processedBy = (transaction as any).assigned_to_processor_name as string | undefined;
  const amount = transaction.amount != null ? `$${Number(transaction.amount).toFixed(2)}` : '';
  const saleDate = transaction.created_at
    ? new Date(transaction.created_at).toLocaleString([], { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
    : '';
  const revealed = !!decryptedData;

  const confirmReveal = () => {
    if (onDecryptData && txIdForDecrypt) onDecryptData(txIdForDecrypt, reason || 'Processing review');
    setShowReason(false);
  };

  return ReactDOM.createPortal((
    <div className="pdp-overlay" onClick={onClose}>
      <div className="pdp-panel" onClick={(e) => e.stopPropagation()}>
        <div className="pdp-header">
          <span className="pdp-title">Processing details</span>
          <button type="button" className="pdp-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="pdp-summary">
          <div className="pdp-avatar">{initials}</div>
          <div style={{ marginRight: 'auto', minWidth: 0 }}>
            <div className="pdp-name">{name}</div>
            <div className="pdp-sub">
              {saleDate && <>Sale date · {saleDate} &nbsp;|&nbsp; </>}Processed by · {processedBy || 'Unassigned'}
            </div>
          </div>
          {pinfo && (
            <label className={`pdp-deposit${pinfo.is_deposit ? ' on' : ''}`}
              title={pinfo.is_deposit && pinfo.deposit_marked_by_name
                ? `Marked by ${pinfo.deposit_marked_by_name}${pinfo.deposit_marked_at ? ` on ${new Date(pinfo.deposit_marked_at).toLocaleString()}` : ''}`
                : pinfo.can_toggle_deposit ? 'Mark this sale as a deposit' : 'Only the assigned processor or a team lead and above can change this'}>
              <input type="checkbox" checked={!!pinfo.is_deposit}
                disabled={!pinfo.can_toggle_deposit || depositSaving}
                onChange={(e) => toggleDeposit(e.target.checked)} />
              <span className="pdp-switch" aria-hidden="true" />
              Deposit
            </label>
          )}
          {transaction.category && <span className="pdp-chip pdp-chip-accent">{transaction.category}</span>}
          {status && <span className="pdp-chip pdp-chip-muted">{status.replace(/_/g, ' ')}</span>}
          {amount && <span className="pdp-amount">{amount}</span>}
        </div>

        <div className="pdp-body">
          {onDecryptData && !revealed && (
            <div className="pdp-reveal">
              {!showReason ? (
                <button type="button" className="pdp-btn-secondary" onClick={() => setShowReason(true)}>
                  Reveal sensitive info
                </button>
              ) : (
                <div className="pdp-reason">
                  <input type="text" placeholder="Reason (logged)" value={reason} onChange={(e) => setReason(e.target.value)} />
                  <button type="button" className="pdp-btn" onClick={confirmReveal}>Confirm</button>
                  <button type="button" className="pdp-btn-secondary" onClick={() => setShowReason(false)}>Cancel</button>
                </div>
              )}
            </div>
          )}
          {revealed && <div className="pdp-revealed-note">⚠️ Sensitive values are revealed. This access is logged.</div>}

          <ProcessingSection
            saleId={recordId}
            category={transaction.category}
            decryptedData={decryptedData}
            onSaved={() => { onEdited?.(); loadEditHistory(); }}
            onInfo={setPinfo}
          />

          <div className="pdp-card">
            <CustomerDocumentsSection recordId={recordId} recordTitle={name} />
          </div>

          <div className="pdp-card">
            <div className="pdp-card-head"><span className="pdp-card-title">Activity log</span></div>
            {editHistory.length === 0 ? (
              <p className="pdp-muted" style={{ margin: 0 }}>No edits recorded yet.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {editHistory.map((e) => (
                  <div key={e.id} style={{ borderLeft: '3px solid var(--primary-light)', paddingLeft: '0.75rem' }}>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', fontSize: '0.85rem' }}>
                      <strong style={{ color: 'var(--text-primary)' }}>{e.edited_by_name || e.edited_by || 'Unknown'}</strong>
                      <span style={{ fontSize: '0.7rem', fontWeight: 600, background: 'var(--primary-light)', color: 'var(--primary)', padding: '1px 8px', borderRadius: '999px' }}>{editSourceLabel(e.source)}</span>
                      <span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>{e.edited_at}</span>
                    </div>
                    <ul style={{ margin: '0.35rem 0 0', paddingLeft: '1.1rem' }}>
                      {Object.entries(e.changes || {}).map(([field, ch]) => (
                        <li key={field} style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>
                          <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{field}</span>: {fmtAuditVal(ch?.old)} → {fmtAuditVal(ch?.new)}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="pdp-card">
            <TransactionComments recordId={transaction.id} />
          </div>
        </div>
      </div>

      <style>{`
        .pdp-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: stretch; justify-content: center; z-index: 2000; padding: 0; overflow-y: auto; }
        .pdp-panel { background: var(--bg-secondary, var(--bg-card)); border-radius: 0; width: 100vw; max-width: 100vw; height: 100vh; max-height: 100vh; margin: 0; overflow: hidden; display: flex; flex-direction: column; box-shadow: 0 10px 30px rgba(0,0,0,0.3); }
        .pdp-header { flex: none; display: flex; align-items: center; justify-content: space-between; padding: 0.85rem 1.5rem; border-bottom: 1px solid var(--border-primary); background: var(--bg-card); }
        .pdp-title { font-size: 1.15rem; font-weight: 600; color: var(--text-primary); }
        .pdp-close { background: none; border: none; font-size: 1.6rem; line-height: 1; color: var(--text-secondary); cursor: pointer; padding: 0 4px; }
        .pdp-summary { flex: none; display: flex; align-items: center; gap: 12px; flex-wrap: wrap; padding: 0.7rem 1.5rem; border-bottom: 1px solid var(--border-primary); background: var(--bg-card); }
        .pdp-avatar { width: 40px; height: 40px; border-radius: 50%; background: var(--primary-light); color: var(--primary); display: flex; align-items: center; justify-content: center; font-weight: 600; font-size: 14px; }
        .pdp-name { font-size: 15px; font-weight: 600; color: var(--text-primary); }
        .pdp-sub { font-size: 12px; color: var(--text-secondary); }
        .pdp-chip { font-size: 12px; font-weight: 600; padding: 3px 10px; border-radius: 999px; }
        .pdp-chip-accent { background: var(--primary-light); color: var(--primary); }
        .pdp-chip-muted { background: var(--bg-tertiary, var(--bg-card)); color: var(--text-secondary); border: 1px solid var(--border-primary); text-transform: capitalize; }
        .pdp-amount { font-size: 15px; font-weight: 600; color: var(--text-primary); }
        .pdp-deposit { display: inline-flex; align-items: center; gap: 8px; font-size: 13px; font-weight: 600; color: var(--text-secondary); cursor: pointer; user-select: none; padding: 3px 10px 3px 4px; border-radius: 999px; border: 1px solid var(--border-primary); }
        .pdp-deposit.on { color: var(--primary); border-color: var(--primary); background: var(--primary-light); }
        .pdp-deposit input { position: absolute; opacity: 0; width: 0; height: 0; }
        .pdp-deposit:has(input:disabled) { cursor: default; opacity: 0.75; }
        .pdp-switch { position: relative; width: 30px; height: 17px; border-radius: 999px; background: var(--border-primary); transition: background 0.15s; flex: none; }
        .pdp-switch::after { content: ''; position: absolute; top: 2px; left: 2px; width: 13px; height: 13px; border-radius: 50%; background: #fff; transition: transform 0.15s; }
        .pdp-deposit.on .pdp-switch { background: var(--primary); }
        .pdp-deposit.on .pdp-switch::after { transform: translateX(13px); }
        .pdp-deposit input:focus-visible + .pdp-switch { outline: 2px solid var(--primary); outline-offset: 2px; }
        .pdp-body { flex: 1 1 auto; min-height: 0; padding: 1rem 1.5rem; overflow-y: auto; background: var(--bg-secondary, var(--bg-card)); display: flex; flex-direction: column; gap: 12px; }
        .pdp-reveal { display: flex; }
        .pdp-reason { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
        .pdp-reason input { flex: 1; min-width: 180px; height: 34px; padding: 7px 9px; border: 1px solid var(--input-border, var(--border-primary)); border-radius: 6px; background: var(--input-bg, var(--bg-card)); color: var(--text-primary); font-size: 0.8125rem; }
        .pdp-revealed-note { font-size: 0.78rem; color: var(--warning, #b45309); background: var(--warning-light, transparent); border: 1px solid var(--border-primary); padding: 6px 10px; border-radius: 6px; }
        .pdp-card { background: var(--bg-card); border: 1px solid var(--border-primary); border-radius: 12px; padding: 14px 16px; }
        .pdp-card-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; gap: 10px; }
        .pdp-card-title { font-size: 0.9rem; font-weight: 600; color: var(--text-primary); }
        .pdp-muted { color: var(--text-secondary); font-size: 0.82rem; }
        .pdp-btn { background: var(--primary); color: var(--text-inverse, #fff); border: none; padding: 8px 14px; border-radius: 7px; font-size: 0.8125rem; font-weight: 600; cursor: pointer; }
        .pdp-btn-secondary { background: var(--bg-tertiary, var(--bg-secondary)); color: var(--text-primary); border: 1px solid var(--border-primary); padding: 8px 14px; border-radius: 7px; font-size: 0.8125rem; font-weight: 600; cursor: pointer; }
      `}</style>
    </div>
  ), document.body);
};
